import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, input, output, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable, Subject, catchError, map, of, takeUntil } from 'rxjs';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent, formCheckboxGroup, formRadioGroup, formSelectMulti } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';

import { LinxProfileType } from '@shared/constants';
import { Beneficiaries, FormModel, LinxEnhanceProfileDetails, LinxOtherDocument, LinxRequestBenificiaryDetails } from '@shared/models';
import { CustomerService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';
import { emptySpacesValidator } from '@shared/validators';

import { linxBeneficiaryProfileFields } from './form-fields';

interface EnhanceBeneficiaryFormModel {
  beneficiaryType: LinxProfileType;
  beneficiaryIds: string[];
  personalInfo: Record<string, boolean>;
  documentTypes: Record<string, boolean>;
  addresses: Record<string, boolean>;
}

const OTHER_DOCUMENT_TYPE = 'other';

type OtherDocumentForm = FormGroup<{ name: FormControl<string>; description: FormControl<string> }>;

/** Names are the natural key for "other" documents — they must be unique per config (case-insensitive, trimmed). */
const uniqueOtherDocumentNames = (control: AbstractControl): ValidationErrors | null => {
  const names = (control as FormArray).controls
    .map((group) => (group.get('name')?.value ?? '').trim().toLowerCase())
    .filter((name: string) => name.length);
  return new Set(names).size === names.length ? null : { duplicateName: true };
};

@Component({
  selector: 'app-enhance-beneficiary-edit',
  templateUrl: './enhance-beneficiary-edit.component.html',
  imports: [RktButtonDirective, FormsModule, ReactiveFormsModule, RktFormComponent, RktIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnhanceBeneficiaryEditComponent implements OnInit, OnDestroy {
  customerService = inject(CustomerService);

  entityId = input<string>();

  isTemplate = input<boolean>(false);

  profileType = input<LinxProfileType | undefined>(LinxProfileType.New);

  selectedEnhanceBeneficiaryDetails = input<LinxRequestBenificiaryDetails | undefined>(undefined);

  selectedBeneficiaryIds = signal<string[]>([]);

  beneficiariesList: Beneficiaries[] = [];

  continue = output<LinxRequestBenificiaryDetails>();

  beneficiaryProfileTypeForm = new FormGroup({});

  formModel: FormModel<EnhanceBeneficiaryFormModel> = {
    beneficiaryType: LinxProfileType.New,
  };

  otherDocumentsForm = new FormGroup({
    otherDocuments: new FormArray<OtherDocumentForm>([], uniqueOtherDocumentNames),
  });

  isOtherSelected = signal<boolean>(false);

  get otherDocuments(): FormArray<OtherDocumentForm> {
    return this.otherDocumentsForm.controls.otherDocuments;
  }

  beneficiaryProfileTypeFields = [
    formRadioGroup({
      key: 'beneficiaryType',
      label: '',
      className: 'linx-template-customer-radio-buttons',
      props: {
        required: true,
        options: [
          { label: 'Onboard Beneficial Owners', value: 'NEW' },
          { label: 'Enhance Existing Beneficial Owners', value: 'EXISTING' },
        ],
      },
      expressions: {
        hide: () => this.profileType() === LinxProfileType.New,
      },
    }),
    formSelectMulti({
      key: 'beneficiaryIds',
      label: 'Existing Beneficiary',
      className: 'linx-form-field-dropdown',
      props: {
        options: [],
        placeholder: 'Select Beneficiary',
        required: true,
        change: (field: FormlyFieldConfig) => this.onBenificiariesSelected(field),
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => field.model.beneficiaryType !== LinxProfileType.Existing || this.isTemplate(),
        'props.options': () => this.beneficiaries$,
      },
    }),
  ];

  documentFields = [
    formCheckboxGroup({
      key: 'documentTypes',
      label: '',
      className: 'linx-checkbox-enhance-fields',
      props: {
        options: [
          { value: 'driving-license', label: 'Drivers License' },
          { value: 'passport', label: 'Passport' },
          { value: 'other', label: 'Other' },
        ],
      },
    }),
  ];

  profileFormModel: FormModel<Record<string, Record<string, boolean> | string>> = {};

  beneficiaries$: Observable<Beneficiaries[] | null> = of([]);

  private destroy$ = new Subject<void>();

  profileFields = computed(() => linxBeneficiaryProfileFields(this.disableEnhanceFields()));

  disableEnhanceFields = computed(() => {
    if (this.selectedBeneficiaryIds().length === 1) {
      const beneficiary = this.beneficiariesList.find((b) => b.id === this.selectedBeneficiaryIds()[0]);
      return beneficiary?.verification !== 'UNVERIFIED';
    }
    return false;
  });

  ngOnInit(): void {
    this.getBusinessCustomerBeneficeries();
    this.setEnhanceProfile();

    this.beneficiaryProfileTypeForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncOtherDocumentsVisibility();
    });
  }

  private syncOtherDocumentsVisibility(): void {
    const selected = !!this.formModel.documentTypes?.[OTHER_DOCUMENT_TYPE];
    if (selected === this.isOtherSelected()) {
      return;
    }
    this.isOtherSelected.set(selected);
    if (selected) {
      if (!this.otherDocuments.length) {
        this.addOtherDocument();
      }
    } else {
      this.otherDocuments.clear();
    }
  }

  private buildOtherDocumentGroup(document?: LinxOtherDocument): OtherDocumentForm {
    return new FormGroup({
      name: new FormControl(document?.name ?? '', { nonNullable: true, validators: [Validators.required, emptySpacesValidator] }),
      description: new FormControl(document?.description ?? '', { nonNullable: true }),
    });
  }

  addOtherDocument(document?: LinxOtherDocument): void {
    this.otherDocuments.push(this.buildOtherDocumentGroup(document));
  }

  removeOtherDocument(index: number): void {
    this.otherDocuments.removeAt(index);
    if (!this.otherDocuments.length) {
      this.addOtherDocument();
    }
  }

  private buildOtherDocuments(): LinxOtherDocument[] {
    return this.otherDocuments.controls
      .map(({ value }) => {
        const name = value.name?.trim() ?? '';
        const description = value.description?.trim();
        return description ? { name, description } : { name };
      })
      .filter((document) => document.name.length);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBenificiariesSelected(field: FormlyFieldConfig): void {
    this.selectedBeneficiaryIds.set(field.formControl?.value || []);
  }

  getBusinessCustomerBeneficeries(): void {
    if (!this.entityId()) return;

    this.beneficiaries$ = this.customerService.getBusinessCustomerBeneficiaries(this.entityId()!).pipe(
      map((beneficiaries) => {
        this.beneficiariesList = beneficiaries || [];
        return beneficiaries.map((beneficiary) => {
          return {
            label: beneficiary.fullName,
            value: beneficiary.id,
            ...beneficiary,
          };
        });
      }),
      catchError((error?: string) => {
        ErrorUtils.catchError('customerService.getBusinessCustomerBeneficiaries error', error);
        return of([]);
      }),
      takeUntil(this.destroy$),
    );
  }

  onClickContinue(): void {
    const { beneficiaryType = LinxProfileType.New, beneficiaryIds, personalInfo, documentTypes = {}, addresses } = this.formModel;

    const profileDetails: LinxEnhanceProfileDetails = {};

    const personalInfoDetails = personalInfo ? Object.keys(personalInfo).filter((key) => personalInfo[key]) : [];
    if (personalInfoDetails.length) {
      const beneficiaryInfo: string[] = [];
      personalInfoDetails.forEach((item: string) => {
        if (item === 'email') {
          profileDetails.email = ['PRIMARY'];
        }
        if (item === 'phoneNumber') {
          profileDetails.phoneNumber = ['HOME'];
        } else {
          beneficiaryInfo.push(item);
        }
      });
      if (beneficiaryInfo.length) {
        profileDetails.personalInfo = beneficiaryInfo;
      }
    }

    const addressesDetails = addresses ? Object.keys(addresses).filter((key) => addresses[key]) : [];
    if (addressesDetails.length) {
      profileDetails.addresses = addressesDetails;
    }

    const selectedDocumentTypes = Object.keys(documentTypes).filter((key) => documentTypes[key]);

    const beneficiaryDetails: LinxRequestBenificiaryDetails = {
      beneficiaryType,
      beneficiaryIds,
      enhanceProfileDetails: Object.keys(profileDetails).length ? profileDetails : undefined,
      documentTypes: selectedDocumentTypes,
    };

    if (selectedDocumentTypes.includes(OTHER_DOCUMENT_TYPE)) {
      beneficiaryDetails.otherDocuments = this.buildOtherDocuments();
    }

    this.continue.emit(beneficiaryDetails);
  }

  onClearBtnClick(): void {
    this.beneficiaryProfileTypeForm.reset();
    this.otherDocuments.clear();
    this.isOtherSelected.set(false);
  }

  private setEnhanceProfile(): void {
    if (!this.selectedEnhanceBeneficiaryDetails) return;
    const { beneficiaryType, enhanceProfileDetails, documentTypes, otherDocuments } = this.selectedEnhanceBeneficiaryDetails() ?? {};
    this.formModel = {
      beneficiaryType: beneficiaryType,
    };

    const { personalInfo, addresses } = enhanceProfileDetails ?? {};

    if (personalInfo?.length) {
      const personalFormInfo = {} as Record<string, boolean>;
      personalInfo.forEach((info: string) => {
        personalFormInfo[info] = true;
      });
      this.formModel.personalInfo = personalFormInfo;
    }

    if (addresses?.length) {
      const addressesInfo = {} as Record<string, boolean>;
      addresses.forEach((address: string) => {
        addressesInfo[address] = true;
      });
      this.formModel.addresses = addressesInfo;
    }

    if (documentTypes?.length) {
      const formDocumentTypes = {} as Record<string, boolean>;
      documentTypes.forEach((type: string) => {
        formDocumentTypes[type] = true;
      });
      this.formModel.documentTypes = formDocumentTypes;
    }

    this.otherDocuments.clear();
    if (documentTypes?.includes(OTHER_DOCUMENT_TYPE)) {
      this.isOtherSelected.set(true);
      if (otherDocuments?.length) {
        otherDocuments.forEach((document) => this.addOtherDocument(document));
      } else {
        this.addOtherDocument();
      }
    }
  }
}
